import type { NumberRequest } from "./types";

/**
 * How far the client has got with *their* part of the request.
 *
 * The detail page is a list of blocks the client fills in one by one, and the
 * question they actually ask ("am I nearly done?") is answered nowhere by the
 * state machine — `documents_pending` says a piece is missing, not how many.
 * So the blocks are counted here, once, and the percentage is shown on the one
 * process step that belongs to the client.
 *
 * Only *required* blocks count: the desired prefix, the number being forwarded
 * and the free-text notes are optional, and a request that can be filed while
 * they are empty must not read as incomplete. Which leaves the documents for
 * both number-producing kinds, and the setup description for a SIP request.
 */
export interface NumberRequestCompletion {
  /** Required blocks already filled. */
  done: number;
  /** Required blocks in total — 0 only if there is nothing to fill in. */
  total: number;
  /** `done / total`, rounded. 100 when there is nothing to fill in. */
  percent: number;
  complete: boolean;
}

/**
 * The process step the percentage is attached to. It is the first step for all
 * three kinds — by design: the client's own work always comes first, everything
 * after it is ours or the operator's.
 */
export const COMPLETION_STEP = 0;

export function getNumberRequestCompletion(
  request: NumberRequest,
): NumberRequestCompletion {
  const parts: boolean[] = [];

  // A SIP request has no checklist: describing the setup *is* the work.
  if (request.kind === "sip_trunk") {
    parts.push(Boolean((request.notes || "").trim()));
  }

  for (const document of request.documents?.items || []) {
    parts.push(document.provided);
  }

  const total = parts.length;
  const done = parts.filter(Boolean).length;

  return {
    done,
    total,
    percent: total === 0 ? 100 : Math.round((done / total) * 100),
    complete: done === total,
  };
}
